import React, { useState, useEffect } from "react";
import { Card, Grid, Icon, TextField } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import {
  getEmployeeProductivityReportList,
  exportEmployeeProductivityReportData,
} from "actions/reports";
import { useDispatch, useSelector } from "react-redux";
import { employeeActivityReportList } from "constants/reports";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Autocomplete } from "@mui/material";
import { autoCompleteProps } from "components/CommonComponent/CommonFunction";
import {
  DATE_RANGE_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
  activityTypeOptions,
} from "components/common/constant";
import { getShiftList } from "actions/shift";
import { getDutyTypeList } from "actions/dutyType";
import { getProductList } from "actions/product";
import { getSubActivityUniqueList } from "actions/subActivity";
import { getMicroActivityUniqueList } from "actions/microActivity";
import { getUnitListing } from "actions/unit";
import { getCustomerList } from "actions/customer";
import { getProjectList } from "actions/project";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { getDepartmentList } from "actions/department";

function EmployeeActivityReport() {
  const dispatch = useDispatch();
  const [pageNum, setPageNum] = useState(1);
  const [filters, setFilters] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();

    const startDateString = startDate.toISOString().split("T")[0];
    const endDateString = endDate.toISOString().split("T")[0];

    return {
      dateRange: "TODAY",
      startDate: startDateString,
      endDate: endDateString,

      lhbNo: "",
      employeeName: "",
      employeeID: "",
      departmentName: "",
      dutyType: "",
      shift: "",
      productType: "",
      customerName: "",
      projectNo: "",
      unitNo: "",
      subUnitNo: "",
      rating: "",
      station: "",
      activityCategory: "",
      subActivity: "",
      microActivity: "",
      hours: "",
      optimalHours: "",
      activityTypeRemark: "",
      remark: "",

      filterParams: JSON.stringify([
        {
          field: "Date",
          condition: "gte",
          filterValues: [startDateString],
        },
        {
          field: "Date",
          condition: "lte",
          filterValues: [endDateString],
        },
      ]),
    };
  });

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [showClearButton, setShowClearButton] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const { notify } = useNotify();
  const reportListData = useSelector(
    (state) => state.employeeActivityReport?.employeeProductivityReportList,
  );
  const shiftListData = useSelector((state) => state.shift.shiftList);

  const dutyTypeListData = useSelector((state) => state.dutyType.dutyTypeList);

  const productListData = useSelector((state) => state.product.productList);

  const subActivityData = useSelector(
    (state) => state.subActivity.subActivityUniqueList,
  );

  const customerListData = useSelector((state) => state.customer.customerList);

  const projectListData = useSelector((state) => state.project.projectList);

  const microActivityData = useSelector(
    (state) => state.microActivity.microActivityUniqueList,
  );

  const unitListingData = useSelector((state) => state.unit.unitList);

  const departmentListData = useSelector((state) => state.department.departmentList,);

  useEffect(() => {
    getEmployeeProductivityReportList(dispatch, pageNum, filters.filterParams);

    return () => {
      dispatch({
        type: employeeActivityReportList,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    if (reportListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords:
          reportListData?.data?.EmployeeProductivityResponse?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (reportListData?.code || reportListData?.message) {
      setListState({
        isLoading: false,
        error:
          reportListData?.message ||
          "Failed to load employee productivity report!",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [reportListData]);

  useEffect(() => {
    getShiftList(dispatch);
    getDutyTypeList(dispatch);
    getProductList(dispatch);
    getSubActivityUniqueList(dispatch);
    getMicroActivityUniqueList(dispatch);
    getUnitListing(dispatch);
    getCustomerList(dispatch);
    getProjectList(dispatch);
    getDepartmentList(dispatch);
  }, [dispatch]);

  const getDateRange = (days) => {
    const today = new Date();

    if (days === "TODAY") {
      const todayString = today.toISOString().split("T")[0];

      return {
        startDate: todayString,
        endDate: todayString,
      };
    }

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);

    if (days === "1") {
      const yesterdayString = endDate.toISOString().split("T")[0];

      return {
        startDate: yesterdayString,
        endDate: yesterdayString,
      };
    }

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (Number(days) - 1));

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const shiftOptions =
    shiftListData?.data?.shift?.map((s) => ({
      id: s.ID,
      name: s.name,
      startTime: s.startTime,
      endTime: s.endTime,
      status: s.status,
    })) || [];

  const dutyTypeOptions =
    dutyTypeListData?.data?.DutyType?.map((item) => ({
      id: item.ID,
      name: item.name,
    })) || [];

  const subActivityOptions =
    subActivityData?.data?.map((item) => ({
      id: item.ID,
      name: item.name,
    })) || [];

  const microActivityOptions =
    microActivityData?.data?.microActivities?.map((item) => ({
      id: item.ID,
      name: item.name,
    })) || [];

  const customerOptions =
    customerListData?.data?.Customer?.map((item) => ({
      id: item.ID,
      name: item.name,
    })) || [];

  const projectOptions =
    projectListData?.data?.project?.map((item) => ({
      id: item.ID,
      projectNo: item.projectNo,
    })) || [];

  const products = productListData?.data?.Product || [];

  const units = unitListingData?.data?.Unit || [];

  const buildFilterParams = () => {
    const filterParams = [];

    filterParams.push({
      field: "Date",
      condition: "gte",
      filterValues: [filters.startDate],
    });

    filterParams.push({
      field: "Date",
      condition: "lte",
      filterValues: [filters.endDate],
    });

    if (filters.lhbNo) {
      filterParams.push({
        field: "LHBNo",
        condition: "eq",
        filterValues: [filters.lhbNo],
      });
    }

    if (filters.employeeName) {
      filterParams.push({
        field: "EmployeeName",
        condition: "like",
        filterValues: [filters.employeeName],
      });
    }

    if (filters.employeeID) {
      filterParams.push({
        field: "EmployeeID",
        condition: "like",
        filterValues: [filters.employeeID],
      });
    }

    if (filters.dutyType) {
      filterParams.push({
        field: "DutyType",
        condition: "like",
        filterValues: [filters.dutyType],
      });
    }

    if (filters.shift) {
      filterParams.push({
        field: "Shift",
        condition: "like",
        filterValues: [filters.shift],
      });
    }

    if (filters.productType) {
      filterParams.push({
        field: "ProductType",
        condition: "like",
        filterValues: [filters.productType],
      });
    }

    if (filters.customerName) {
      filterParams.push({
        field: "CustomerName",
        condition: "like",
        filterValues: [filters.customerName],
      });
    }

    if (filters.departmentName) {
        const selectedDepartment =
          departmentListData?.data?.Department?.find(
            (item) => item.ID === filters.departmentName,
          );

        if (selectedDepartment) {
          filterParams.push({
            field: "DepartmentName",
            condition: "like",
            filterValues: [selectedDepartment.name],
          });
        }
      }

    if (filters.projectNo) {
      filterParams.push({
        field: "ProjectNo",
        condition: "like",
        filterValues: [filters.projectNo],
      });
    }

    if (filters.unitNo) {
      filterParams.push({
        field: "UnitNo",
        condition: "like",
        filterValues: [filters.unitNo],
      });
    }

    if (filters.subUnitNo) {
      filterParams.push({
        field: "SubUnitNo",
        condition: "like",
        filterValues: [filters.subUnitNo],
      });
    }

    if (filters.rating) {
      filterParams.push({
        field: "Rating",
        condition: "eq",
        filterValues: [filters.rating],
      });
    }

    if (filters.station) {
      filterParams.push({
        field: "Station",
        condition: "like",
        filterValues: [filters.station],
      });
    }

    if (filters.activityCategory) {
      filterParams.push({
        field: "ActivityCategory",
        condition: "eq",
        filterValues: [filters.activityCategory],
      });
    }

    if (filters.subActivity) {
      filterParams.push({
        field: "SubActivity",
        condition: "like",
        filterValues: [filters.subActivity],
      });
    }

    if (filters.microActivity) {
      filterParams.push({
        field: "MicroActivity",
        condition: "like",
        filterValues: [filters.microActivity],
      });
    }

    if (filters.hours) {
      filterParams.push({
        field: "Hours",
        condition: "gte",
        filterValues: [filters.hours],
      });
    }

    if (filters.optimalHours) {
      filterParams.push({
        field: "OptimalHours",
        condition: "gte",
        filterValues: [filters.optimalHours],
      });
    }

    if (filters.activityTypeRemark) {
      filterParams.push({
        field: "ActivityTypeRemark",
        condition: "eq",
        filterValues: [filters.activityTypeRemark],
      });
    }

    if (filters.remark) {
      filterParams.push({
        field: "Remark",
        condition: "like",
        filterValues: [filters.remark],
      });
    }

    return JSON.stringify(filterParams);
  };

  const wrapCell = (width) => (cell) => (
    <div
      style={{
        width,
        overflowWrap: "break-word",
        wordBreak: "break-word",
        whiteSpace: "normal",
        lineHeight: "1.4",
      }}
    >
      {cell.value}
    </div>
  );

  const columns = [
    { Header: "Sr No", accessor: "srNo", width: "115px" },
    { Header: "LHB No", accessor: "lhbNo", width: "125px" },
    {
      Header: "Employee Name",
      accessor: "employeeName",
      width: "190px",
      Cell: wrapCell("190px"),
    },
    { Header: "Employee ID", accessor: "employeeID", width: "165px" },
    { Header: "Department Name", accessor: "departmentName", width: "165px"},
    {
      Header: "Duty Type",
      accessor: "dutyType",
      width: "150px",
      Cell: wrapCell("150px"),
    },
    { Header: "Date", accessor: "date", width: "110px" },
    { Header: "Shift", accessor: "shift", width: "115px" },
    {
      Header: "Product Type",
      accessor: "productType",
      width: "175px",
      Cell: wrapCell("175px"),
    },
    {
      Header: "Customer Name",
      accessor: "customerName",
      width: "195px",
      Cell: wrapCell("195px"),
    },
    { Header: "Project No", accessor: "projectNo", width: "160px" },
    { Header: "Unit No", accessor: "unitNo", width: "130px" },
    { Header: "Sub Unit No", accessor: "subUnitNo", width: "160px" },
    { Header: "Rating", accessor: "rating", width: "125px" },
    {
      Header: "Station",
      accessor: "station",
      width: "180px",
      Cell: wrapCell("180px"),
    },
    {
      Header: "Activity Type",
      accessor: "activityType",
      width: "175px",
      Cell: wrapCell("175px"),
    },
    {
      Header: "Sub Activity",
      accessor: "subActivity",
      width: "200px",
      Cell: wrapCell("200px"),
    },
    {
      Header: "Micro Activity",
      accessor: "microActivity",
      width: "220px",
      Cell: wrapCell("220px"),
    },
    {
      Header: "Hours",
      accessor: "hours",
      width: "120px",
      Cell: wrapCell("120px"),
    },
    {
      Header: "Optimal Hours",
      accessor: "optimalHours",
      width: "190px",
      Cell: wrapCell("190px"),
    },
    {
      Header: "Activity Type - Remark",
      accessor: "activityTypeRemark",
      width: "250px",
      Cell: wrapCell("250px"),
    },
    {
      Header: "Remark",
      accessor: "remark",
      width: "180px",
      Cell: wrapCell("180px"),
    },
  ];

  const data =
    reportListData?.data?.EmployeeProductivityResponse?.map((item, index) => ({
      srNo:
        (pageNum - 1) * (reportListData?.data?.noOfRecordsPerPage || 25) +
        index +
        1,

      lhbNo: item.lhbNo,
      employeeName: item.employeeName,
      employeeID: item.employeeID,
      departmentName: item.departmentName,
      dutyType: item.dutyType,
      date: item.date?.split("T")[0] || "",
      shift: item.shift,
      productType: item.productType,
      customerName: item.customerName,
      projectNo: item.projectNo,
      unitNo: item.unitNo,
      subUnitNo: item.subUnitNo,
      rating: item.rating,
      station: item.station,
      activityType: item.activityType,
      subActivity: item.subActivity,
      microActivity: item.microActivity,
      hours: item.hours,
      optimalHours: item.optimalHours,
      activityTypeRemark: item.activityTypeRemark,
      remark: item.remark,
    })) || [];

  const handleSearch = () => {
    const filterParams = buildFilterParams();

    if (filterParams === filters.filterParams) {
      return;
    }

    setListState((prev) => ({
      ...prev,
      isLoading: true,
      showLoaderOnClick: true,
    }));

    setFilters((prev) => ({
      ...prev,
      filterParams,
    }));

    setShowClearButton(true);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleClear = () => {
    const endDate = new Date();
    const startDate = new Date();

    startDate.setDate(endDate.getDate());

    const startDateString = startDate.toISOString().split("T")[0];
    const endDateString = endDate.toISOString().split("T")[0];

    setFilters({
      dateRange: "TODAY",
      startDate: startDateString,
      endDate: endDateString,

      lhbNo: "",
      employeeName: "",
      employeeID: "",
      departmentName:"",
      dutyType: "",
      shift: "",
      productType: "",
      customerName: "",
      projectNo: "",
      unitNo: "",
      subUnitNo: "",
      rating: "",
      station: "",
      activityCategory: "",
      subActivity: "",
      microActivity: "",
      hours: "",
      optimalHours: "",
      activityTypeRemark: "",
      remark: "",

      filterParams: JSON.stringify([
        {
          field: "Date",
          condition: "gte",
          filterValues: [startDateString],
        },
        {
          field: "Date",
          condition: "lte",
          filterValues: [endDateString],
        },
      ]),
    });

    setShowClearButton(false);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleExportExcel = () => {};

  const handleExportPDF = () => {};

  const downloadEmployeeProductivityReportCSV = async () => {
    try {
      setExportLoading(true);

      const res = await exportEmployeeProductivityReportData(filters.filterParams);
      
      const url = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "EmployeeProductivityReport.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      notify(
        res?.message ||
          "EmployeeProductivityReport file downloaded successfully",
        "success",
      );
    } catch (error) {
      notify("Failed to download employeeProductivityReport file", "error");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Employee Productivity Report
                </MDTypography>

                <MDBox display="flex" gap={2}>
                  {/* <MDButton
                    variant="gradient"
                    color="light"
                    startIcon={<Icon>download</Icon>}
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      textTransform: "none",
                    }}
                    onClick={handleExportExcel}
                  >
                    Export Excel
                  </MDButton>

                  <MDButton
                    variant="gradient"
                    color="light"
                    startIcon={<Icon>picture_as_pdf</Icon>}
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      textTransform: "none",
                    }}
                    onClick={handleExportPDF}
                  >
                    Export PDF
                  </MDButton> */}
                  <MDButton
                    variant="gradient"
                    color="light"
                    startIcon={<Icon>file_upload</Icon>}
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      textTransform: "none",
                    }}
                    onClick={downloadEmployeeProductivityReportCSV}
                  >
                    {exportLoading ? (
                      <>
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Exporting...
                      </>
                    ) : (
                      "Export Report"
                    )}
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox px={3} pb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={DATE_RANGE_OPTIONS}
                      value={
                        DATE_RANGE_OPTIONS.find(
                          (item) => item.value === filters.dateRange,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) => {
                        if (!value) return;

                        if (value.value === "CUSTOM") {
                          setFilters({
                            ...filters,
                            dateRange: "CUSTOM",
                          });
                        } else {
                          const dates = getDateRange(value.value);

                          setFilters({
                            ...filters,
                            dateRange: value.value,
                            startDate: dates.startDate,
                            endDate: dates.endDate,
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Date Range"
                        />
                      )}
                    />
                  </Grid>

                  {filters.dateRange === "CUSTOM" && (
                    <>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="Start Date"
                            value={dayjs(filters.startDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                startDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="End Date"
                            minDate={dayjs(filters.startDate)}
                            value={dayjs(filters.endDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                endDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="LHB No (e.g. LHB-123 or 123)"
                      value={filters.lhbNo}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          lhbNo: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by Name"
                      value={filters.employeeName}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          employeeName: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by Emp ID"
                      value={filters.employeeID}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          employeeID: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={departmentListData?.data?.Department || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        departmentListData?.data?.Department?.find(
                          (item) => item.ID === filters.departmentName,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          departmentName: newValue?.ID || "",
                        }))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Department"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={projectOptions}
                      value={
                        projectOptions.find(
                          (item) => item.projectNo === filters.projectNo,
                        ) || null
                      }
                      getOptionLabel={(option) => option.projectNo || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          projectNo: value?.projectNo || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Project No"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      size="small"
                      options={shiftOptions.filter(
                        (s) => s.status === "ACTIVE",
                      )}
                      value={
                        shiftOptions.find((s) => s.name === filters.shift) ||
                        null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      onChange={(event, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          shift: newValue?.name || "",
                        }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Shift" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={customerOptions}
                      value={
                        customerOptions.find(
                          (item) => item.name === filters.customerName,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          customerName: value?.name || "",
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Customer" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={activityTypeOptions || []}
                      getOptionLabel={(option) => option.label || ""}
                      value={
                        activityTypeOptions?.find(
                          (type) => type.value === filters.activityCategory,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          activityCategory: newValue?.value || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Activity type"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={dutyTypeOptions}
                      value={
                        dutyTypeOptions.find(
                          (item) => item.name === filters.dutyType,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          dutyType: value?.name || "",
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Duty Type" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      size="small"
                      options={products}
                      getOptionLabel={(option) => option.type || ""}
                      value={
                        products.find((p) => p.type === filters.productType) ||
                        null
                      }
                      onChange={(e, newValue) =>
                        setFilters({
                          ...filters,
                          productType: newValue?.type || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select product type"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      size="small"
                      options={units.filter((u) => u.status === "ACTIVE")}
                      getOptionLabel={(option) => option.unitNo || ""}
                      value={
                        units.find((u) => u.unitNo === filters.unitNo) || null
                      }
                      onChange={(e, newValue) =>
                        setFilters({
                          ...filters,
                          unitNo: newValue?.unitNo || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Unit" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={subActivityOptions}
                      value={
                        subActivityOptions.find(
                          (item) => item.name === filters.subActivity,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          subActivity: value?.name || "",
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Sub Activity"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={microActivityOptions}
                      value={
                        microActivityOptions.find(
                          (item) => item.name === filters.microActivity,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          microActivity: value?.name || "",
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Micro Activity"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg="auto">
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        onClick={handleSearch}
                      >
                        <Icon sx={{ mr: 1 }}>search</Icon>
                        Search
                      </MDButton>

                      {showClearButton && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue">
                  Total Records: {reportListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No records found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={data || []}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (reportListData?.data?.totalRecords || 0) /
                        (reportListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default EmployeeActivityReport;
